import { useCallback, useRef, useState } from 'react';
import { clearChatSession } from './chatSessionNavigation';
import { CHAT_CONNECTION_ERROR_MESSAGE, isChatConnectionError } from './chatConnectionError';

const RESTORE_ERROR = 'Unable to open that chat. It may no longer be available.';

export default function useChatSidebar({
  sessionId,
  processing,
  restoring,
  exporting,
  restoreSessionById,
  refreshSessions,
  onRestoreFailure,
  setRestoring,
  setProcessing,
  setProcessingId,
}) {
  const [restoreError, setRestoreError] = useState(null);
  const [retrySessionId, setRetrySessionId] = useState(null);
  const busy = processing || restoring || Boolean(exporting);
  const busyRef = useRef(busy);
  const sessionIdRef = useRef(sessionId);
  busyRef.current = busy;
  sessionIdRef.current = sessionId;

  const restoreSessionWithGuard = useCallback(
    async (nextSessionId, options = {}) => {
      const isCancelled = typeof options.isCancelled === 'function'
        ? options.isCancelled
        : () => false;
      if (!nextSessionId || (busyRef.current && !options.allowBusy)) return false;
      if (nextSessionId === sessionIdRef.current) return true;

      setRestoring(true);
      setRestoreError(null);
      setRetrySessionId(null);
      try {
        await restoreSessionById(nextSessionId, { isCancelled });
        if (isCancelled()) return false;
        refreshSessions?.();
        return true;
      } catch (error) {
        if (!isCancelled()) {
          // A dropped request says nothing about the chat itself (FE #746): keep the
          // stored session and offer a retry instead of calling it unavailable.
          if (isChatConnectionError(error)) {
            setRetrySessionId(nextSessionId);
            setRestoreError(CHAT_CONNECTION_ERROR_MESSAGE);
            return false;
          }
          if (options.clearOnFailure) {
            clearChatSession();
            onRestoreFailure?.();
          }
          setRestoreError(RESTORE_ERROR);
        }
        return false;
      } finally {
        if (!isCancelled()) {
          setRestoring(false);
          setProcessing(false);
          setProcessingId(null);
        }
      }
    },
    [
      onRestoreFailure,
      refreshSessions,
      restoreSessionById,
      setProcessing,
      setProcessingId,
      setRestoring,
    ]
  );

  const handleSelectSession = useCallback(
    (nextSessionId) => restoreSessionWithGuard(nextSessionId),
    [restoreSessionWithGuard]
  );

  const clearRestoreError = useCallback(() => {
    setRestoreError(null);
    setRetrySessionId(null);
  }, []);

  const retryRestore = useCallback(
    () => restoreSessionWithGuard(retrySessionId, { allowBusy: true }),
    [restoreSessionWithGuard, retrySessionId]
  );

  return {
    clearRestoreError,
    restoreError,
    restoreRetry: retrySessionId ? retryRestore : null,
    restoreSessionWithGuard,
    sidebarProps: {
      restoreError,
      selectedSessionId: sessionId,
      disabled: busy,
      onSelectSession: handleSelectSession,
    },
  };
}
